import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Activity,
  AlertOctagon,
  CalendarRange,
  Loader2,
  RefreshCw,
  Shield,
  ShieldCheck,
  Trophy,
  Users,
  Wallet,
} from 'lucide-react';
import { api, friendlyError, unwrap } from '@/lib/api';
import { ROUTES } from '@/lib/constants';
import { ADMIN } from '@/constants/testIds';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const TONE_MAP = {
  gold: 'text-[var(--ns-gold-700)] bg-[var(--ns-amber-100)] border-[var(--ns-gold)]/40',
  green: 'text-[var(--ns-green-700)] bg-[var(--ns-green)]/12 border-[var(--ns-green)]/40',
  amber: 'text-[var(--ns-gold-700)] bg-[var(--ns-amber-50)] border-[var(--ns-gold)]/30',
  red: 'text-[var(--ns-red)] bg-[var(--ns-red)]/10 border-[var(--ns-red)]/30',
  blue: 'text-[var(--ns-blue)] bg-[var(--ns-blue)]/10 border-[var(--ns-blue)]/30',
};

const Kpi = ({ icon: Icon, label, value, tone = 'gold', testId }) => (
  <Card data-testid={testId} className="bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)]">
    <CardContent className="p-5">
      <div className={`h-10 w-10 rounded-xl border flex items-center justify-center mb-3 ${TONE_MAP[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-[11px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">{label}</p>
      <p className="font-display text-3xl text-[var(--ns-text)] mt-1">{value}</p>
    </CardContent>
  </Card>
);

export default function AdminDashboard() {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState({ total: 0, active: 0, pending: 0, suspended: 0 });
  const [seedStatus, setSeedStatus] = useState(null);
  const [reseeding, setReseeding] = useState(false);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const fetchCount = async (status) => {
        const res = await api.get('/admin/users/', {
          params: { page: 1, page_size: 1, ...(status ? { status } : {}) },
        });
        return unwrap(res).total || 0;
      };
      const [total, active, pendingA, pendingB, suspended, seedRes] = await Promise.all([
        fetchCount(),
        fetchCount('active'),
        fetchCount('pending_email_verification'),
        fetchCount('pending_guardian_consent'),
        fetchCount('suspended'),
        api.get('/admin/dev-seed/status').then(unwrap).catch(() => null),
      ]);
      setKpis({ total, active, pending: pendingA + pendingB, suspended });
      setSeedStatus(seedRes);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  const handleReseed = async () => {
    if (reseeding) return;
    setReseeding(true);
    try {
      const res = await api.post('/admin/dev-seed');
      const data = unwrap(res);
      toast.success(res?.data?.message || 'Datos de prueba recargados.');
      setSeedStatus({ status: data.status, environment: seedStatus?.environment || 'development' });
      // Refrescar KPIs de usuarios también
      loadAll();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setReseeding(false);
    }
  };

  const seed = seedStatus?.status || {};
  const isProdBlocked = seedStatus?.blocked;

  return (
    <AdminLayout>
      <div className="flex flex-col gap-2 mb-6">
        <span className="text-[11px] uppercase tracking-[0.25em] text-[var(--ns-gold-600)] font-semibold">
          Administración
        </span>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <h1 className="font-display uppercase tracking-tight text-[var(--ns-text)] text-4xl sm:text-5xl leading-[1.02]">
            Panel admin
          </h1>
          {seedStatus && (
            <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full font-bold bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border border-[var(--ns-border)]">
              Entorno: {seedStatus.environment}
            </span>
          )}
        </div>
        <p className="text-sm text-[var(--ns-text-muted)] max-w-2xl">
          Resumen de cuentas, datos seed y accesos rápidos a la gestión.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-[var(--ns-gold-600)]" />
        </div>
      ) : error ? (
        <p className="text-sm text-[var(--ns-red)]">{error}</p>
      ) : (
        <>
          {/* KPIs usuarios */}
          <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
            <Kpi icon={Users} label="Total usuarios" value={kpis.total} tone="gold" testId={ADMIN.totalUsersKpi} />
            <Kpi icon={ShieldCheck} label="Activos" value={kpis.active} tone="green" testId={ADMIN.activeUsersKpi} />
            <Kpi icon={Activity} label="Pendientes" value={kpis.pending} tone="amber" testId={ADMIN.pendingUsersKpi} />
            <Kpi icon={AlertOctagon} label="Suspendidos" value={kpis.suspended} tone="red" testId={ADMIN.suspendedUsersKpi} />
          </div>

          {/* KPIs seed entities */}
          <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--ns-gold-600)] font-semibold">
                Datos seed
              </p>
              <h2 className="font-display uppercase tracking-tight text-[var(--ns-text)] text-2xl sm:text-3xl leading-[1.02]">
                Estado actual
              </h2>
            </div>
            {isSuperAdmin && (
              <Button
                onClick={handleReseed}
                disabled={reseeding || isProdBlocked}
                data-testid="admin-reseed-button"
                className="h-10 px-4 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0"
              >
                {reseeding ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Recargar datos de prueba
                  </>
                )}
              </Button>
            )}
          </div>
          {isProdBlocked && (
            <p className="text-xs text-[var(--ns-red)] mb-3">
              ⚠️ Seed bloqueado en este entorno (producción).
            </p>
          )}
          <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
            <Kpi icon={Trophy} label="Deportes" value={seed.sports || 0} tone="gold" testId="admin-seed-sports" />
            <Kpi icon={Shield} label="Clubes" value={seed.clubs || 0} tone="blue" testId="admin-seed-clubs" />
            <Kpi icon={Users} label="Equipos" value={seed.teams || 0} tone="amber" testId="admin-seed-teams" />
            <Kpi icon={CalendarRange} label="Partidos" value={seed.events || 0} tone="green" testId="admin-seed-events" />
            <Kpi icon={Wallet} label="Planes" value={seed.plans || 0} tone="gold" testId="admin-seed-plans" />
            <Kpi icon={Users} label="Usuarios seed" value={seed.users || 0} tone="blue" testId="admin-seed-users" />
          </div>

          <div className="mt-8">
            <Button
              asChild
              className="h-11 px-5 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0"
            >
              <Link to={ROUTES.adminUsers}>Gestionar usuarios</Link>
            </Button>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
